import type { CsvPreview, ImportReport } from '@numistic/contracts'
import { useCsvColumns, useDataClient, useImport } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '@numistic/ui'
import { createRoute } from '@tanstack/react-router'
import { useId, useState } from 'react'

import { useErrorText } from '../../lib/errors.js'
import { settingsRoute } from './layout.js'

export const dataRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'data',
  component: DataPage,
})

function DataPage() {
  const { t } = useTranslation()
  const client = useDataClient()
  return (
    <div className="grid max-w-4xl gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('io.exportTitle')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('io.exportHint')}</p>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button asChild>
            <a href={client.io.exportUrl('json')} download>
              {t('io.exportJson')}
            </a>
          </Button>
          <Button asChild variant="outline">
            <a href={client.io.exportUrl('csv')} download>
              {t('io.exportCsv')}
            </a>
          </Button>
        </CardContent>
      </Card>
      <ImportCard />
    </div>
  )
}

function ImportCard() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const inputId = useId()
  const columns = useCsvColumns()
  const run = useImport()
  const [file, setFile] = useState<File | null>(null)
  const [mapping, setMapping] = useState<Record<string, string | null> | null>(null)
  const [report, setReport] = useState<ImportReport | null>(null)
  const isCsv = file ? /\.(csv|txt|tsv)$/i.test(file.name) : false

  const choose = (f: File | null) => {
    setFile(f)
    setReport(null)
    setMapping(null)
    run.reset()
    if (f && /\.(csv|txt|tsv)$/i.test(f.name))
      columns.mutate(f, { onSuccess: (p) => setMapping(p.suggested) })
  }
  const submit = (dryRun: boolean) => {
    if (!file) return
    run.mutate({ file, dryRun, ...(isCsv && mapping ? { mapping } : {}) }, { onSuccess: setReport })
  }
  const error = columns.error ?? run.error

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('io.importTitle')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('io.importHint')}</p>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <label
            htmlFor={inputId}
            className="cursor-pointer rounded-md border px-3 py-2 text-sm hover:border-primary"
          >
            {t('io.chooseFile')}
          </label>
          <input
            id={inputId}
            type="file"
            accept=".json,.csv,.tsv,.txt,application/json,text/csv"
            className="sr-only"
            onChange={(e) => choose(e.target.files?.[0] ?? null)}
          />
          {file ? <span className="text-sm">{file.name}</span> : null}
        </div>
        {error ? <Alert variant="destructive">{errorText.request(error)}</Alert> : null}
        {isCsv && columns.data && mapping ? (
          <MappingTable
            preview={columns.data}
            mapping={mapping}
            onChange={(next) => {
              // Другое сопоставление — прежняя проверка к нему не относится: сначала «Проверить».
              setMapping(next)
              setReport(null)
            }}
          />
        ) : null}
        {file && (!isCsv || mapping) ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={run.isPending} onClick={() => submit(true)}>
              {t('io.check')}
            </Button>
            <Button
              disabled={run.isPending || !report?.dryRun || report.imported === 0}
              onClick={() => submit(false)}
            >
              {t('io.import')}
            </Button>
          </div>
        ) : null}
        {report ? <Report report={report} /> : null}
      </CardContent>
    </Card>
  )
}

function MappingTable({
  preview,
  mapping,
  onChange,
}: {
  preview: CsvPreview
  mapping: Record<string, string | null>
  onChange: (m: Record<string, string | null>) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="grid gap-2">
      <p className="text-sm">{t('io.mappingHint', { rows: preview.rows })}</p>
      <Table>
        <THead>
          <Tr>
            <Th>{t('io.column')}</Th>
            <Th>{t('io.sample')}</Th>
            <Th>{t('io.target')}</Th>
          </Tr>
        </THead>
        <TBody>
          {preview.headers.map((h, i) => (
            <Tr key={h}>
              <Td className="font-medium">{h}</Td>
              <Td className="max-w-48 truncate text-muted-foreground">
                {preview.sample.map((r) => r[i]).find(Boolean) ?? ''}
              </Td>
              <Td>
                <Select
                  aria-label={t('io.targetFor', { column: h })}
                  value={mapping[h] ?? ''}
                  onChange={(e) => onChange({ ...mapping, [h]: e.target.value || null })}
                >
                  <option value="">{t('io.skip')}</option>
                  {preview.targets.map((x) => (
                    <option key={x.code} value={x.code}>
                      {x.label}
                    </option>
                  ))}
                </Select>
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  )
}

function Report({ report }: { report: ImportReport }) {
  const { t } = useTranslation()
  const c = report.created
  return (
    <Alert
      variant={report.issues.length ? 'default' : 'default'}
      className="grid gap-2"
      role="status"
    >
      <strong>
        {report.dryRun
          ? t('io.dryRunResult', { imported: report.imported, rows: report.rows })
          : t('io.doneResult', { imported: report.imported, rows: report.rows })}
      </strong>
      <span className="text-sm">
        {t('io.created', {
          collections: c.collections,
          locations: c.locations,
          tags: c.tags,
          items: c.catalogItems,
          events: c.events,
          valuations: c.valuations,
          notes: c.notes,
        })}
      </span>
      {report.skipped ? (
        <span className="text-sm">{t('io.skipped', { count: report.skipped })}</span>
      ) : null}
      {report.issues.length ? (
        <ul className="max-h-60 overflow-auto text-sm" aria-label={t('io.issues')}>
          {report.issues.map((i, k) => (
            <li key={k}>
              {i.row !== null ? t('io.row', { row: i.row }) : null}
              {i.column ? ` · ${i.column}` : ''}:{' '}
              {t(`errors.validation.${i.message}` as 'errors.validation.invalid', {
                defaultValue: i.message,
              })}
            </li>
          ))}
        </ul>
      ) : null}
    </Alert>
  )
}
